/**
 * AdminUniversityGrading — Student-centric assignment & grading dashboard.
 *
 *   • Search/select a student
 *   • View enrolled courses + status counts
 *   • Open a submission → grade it + write feedback (AI-assisted)
 *   • Publish workflow with confirmation modal
 *
 * Compliance:
 *   • Grade writes always carry `graded_by` (the logged-in admin)
 *   • AI feedback is always drafted as `status="draft"` and never reaches the
 *     student until the admin clicks "Publish" and confirms the modal.
 */
import React, { useEffect, useMemo, useState } from "react";
import AdminShell from "@/components/AdminShell";
import { toast } from "sonner";
import { adminAuth } from "@/lib/api";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";
import {
  Loader2, Search, User, BookOpen, Award, Send, Sparkles, Save,
  CheckCircle2, XCircle, Mail, RefreshCcw, AlertCircle, ChevronRight,
} from "lucide-react";

const STATUS_TONE = {
  submitted:              "bg-slate-100 text-slate-700",
  reviewed:               "bg-blue-100 text-blue-800",
  graded:                 "bg-purple-100 text-purple-900",
  passed:                 "bg-emerald-100 text-emerald-800",
  failed:                 "bg-rose-100 text-rose-800",
  returned_for_revision:  "bg-amber-100 text-amber-800",
  missing:                "bg-slate-100 text-slate-500",
};

export default function AdminUniversityGrading() {
  const [q, setQ] = useState("");
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [overview, setOverview] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeSubmission, setActiveSubmission] = useState(null);
  const adminEmail = adminAuth.getEmail?.() || "admin@kcis.com";

  // ── student search ─────────────────────────────────────────────────────
  const search = async (term) => {
    setLoading(true);
    try {
      const r = await universityApi.gradingStudents({ q: term, limit: 30 });
      setStudents(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e, "Search failed")); }
    finally { setLoading(false); }
  };
  useEffect(() => { search(""); }, []);

  // ── load profile when selected ─────────────────────────────────────────
  useEffect(() => {
    if (!selected) { setOverview(null); setSubmissions([]); return; }
    (async () => {
      setLoading(true);
      try {
        const [ov, subs] = await Promise.all([
          universityApi.gradingOverview(selected.student_id),
          universityApi.gradingSubmissions(selected.student_id),
        ]);
        setOverview(ov.data);
        setSubmissions(subs.data?.items || []);
      } catch (e) { toast.error(formatApiError(e, "Could not load student")); }
      finally { setLoading(false); }
    })();
  }, [selected]);

  const refresh = async () => {
    if (!selected) return;
    const subs = await universityApi.gradingSubmissions(selected.student_id);
    setSubmissions(subs.data?.items || []);
  };

  return (
    <AdminShell door="university" title="Assignments / Grading">
      <div data-testid="admin-grading-page" className="space-y-6">
        {/* Search */}
        <section className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              type="text"
              className="form-input"
              placeholder="Search by name or email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search(q)}
              data-testid="grading-search-input"
            />
            <button
              onClick={() => search(q)}
              className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white text-sm h-10 px-4"
              data-testid="grading-search-btn"
            >Search</button>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-72 overflow-y-auto" data-testid="grading-students-list">
            {students.map((s) => (
              <button
                key={s.student_id}
                onClick={() => setSelected(s)}
                className={`text-left rounded-xl border px-3 py-2 transition ${
                  selected?.student_id === s.student_id
                    ? "border-purple-900 bg-purple-50"
                    : "border-slate-200 hover:border-purple-300"
                }`}
                data-testid={`grading-student-${s.student_id}`}
              >
                <div className="text-sm font-medium text-slate-900 truncate">{s.student_name}</div>
                <div className="text-xs text-slate-500 truncate">{s.student_email}</div>
              </button>
            ))}
            {students.length === 0 && !loading && (
              <div className="col-span-full text-sm text-slate-400 p-4 text-center">No students found.</div>
            )}
          </div>
        </section>

        {!selected ? (
          <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
            Select a student above to begin grading.
          </div>
        ) : loading ? (
          <div className="text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : (
          <StudentDetail
            student={selected}
            overview={overview}
            submissions={submissions}
            onOpen={setActiveSubmission}
            refresh={refresh}
          />
        )}

        {activeSubmission && (
          <GradingDrawer
            submission={activeSubmission}
            student={selected}
            adminEmail={adminEmail}
            onClose={() => setActiveSubmission(null)}
            onChanged={async () => { await refresh(); setActiveSubmission(null); }}
          />
        )}
      </div>
    </AdminShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Student detail
// ─────────────────────────────────────────────────────────────────────────────

function StudentDetail({ student, overview, submissions, onOpen, refresh }) {
  const byCourse = useMemo(() => {
    const m = {};
    for (const s of submissions) {
      const c = s.course_code || "—";
      (m[c] = m[c] || []).push(s);
    }
    return m;
  }, [submissions]);

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="inline-flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-100 inline-flex items-center justify-center text-purple-900">
              <User className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display text-lg text-slate-900" data-testid="grading-selected-name">{student.student_name}</div>
              <div className="text-xs text-slate-500">{student.student_email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Stat label="Courses"        value={overview?.courses?.length || 0} />
            <Stat label="Drafts"         value={overview?.feedback_counts?.draft || 0} tone="amber" />
            <Stat label="Published FB"   value={overview?.feedback_counts?.published || 0} tone="green" />
          </div>
        </div>
      </section>

      {Object.keys(byCourse).length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500" data-testid="grading-no-submissions">
          This student has no submissions yet.
        </div>
      ) : Object.entries(byCourse).map(([course, subs]) => (
        <section key={course} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`grading-course-block-${course}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="inline-flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-purple-900" />
              <span className="font-display text-base text-slate-900">{course}</span>
              <span className="text-xs text-slate-500">· {subs.length} submission{subs.length === 1 ? "" : "s"}</span>
            </div>
          </div>
          <div className="divide-y divide-slate-100 rounded-xl border border-slate-100">
            {subs.map((s) => (
              <SubmissionRow key={s.id} submission={s} onOpen={onOpen} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function SubmissionRow({ submission, onOpen }) {
  const g = submission.grade;
  const f = submission.feedback;
  return (
    <button
      onClick={() => onOpen(submission)}
      className="w-full text-left px-3 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition"
      data-testid={`grading-submission-${submission.id}`}
    >
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-900 truncate">{submission.title || submission.kind}</div>
        <div className="text-xs text-slate-500 truncate">
          {submission.kind} · submitted {new Date(submission.submitted_at).toLocaleDateString()}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {g ? (
          <span className="text-xs font-mono text-slate-700">{g.score}/{g.max_score}</span>
        ) : (
          <span className="text-[10px] uppercase tracking-wide text-slate-400">Ungraded</span>
        )}
        <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${STATUS_TONE[submission.status] || "bg-slate-100"}`}>
          {submission.status.replace(/_/g, " ")}
        </span>
        {f?.status === "draft" && (
          <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid="fb-draft-pill">
            FB draft
          </span>
        )}
        {f?.status === "published" && (
          <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
            FB published
          </span>
        )}
        <ChevronRight className="w-4 h-4 text-slate-400" />
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Grading drawer  (the workhorse)
// ─────────────────────────────────────────────────────────────────────────────

function GradingDrawer({ submission, student, adminEmail, onClose, onChanged }) {
  const [score, setScore]       = useState(submission.grade?.score ?? "");
  const [maxScore, setMaxScore] = useState(submission.grade?.max_score ?? 100);
  const [pass, setPass]         = useState(submission.grade?.pass_threshold ?? 70);
  const [ceHrs, setCeHrs]       = useState(submission.grade?.ce_hours_awarded ?? 0);
  const [clockHrs, setClockHrs] = useState(submission.grade?.clock_hours_awarded ?? 0);

  const [feedbackText, setFeedbackText] = useState(submission.feedback?.student_visible_text || "");
  const [privateNotes, setPrivateNotes] = useState(submission.feedback?.instructor_private_notes || "");
  const [aiBusy, setAiBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [gradingBusy, setGradingBusy] = useState(false);
  const [fbId, setFbId] = useState(submission.feedback?.id || null);
  const [fbStatus, setFbStatus] = useState(submission.feedback?.status || null);
  const [aiInfo, setAiInfo] = useState(
    submission.feedback?.ai_generated
      ? { model: submission.feedback.ai_model, at: submission.feedback.ai_generated_at }
      : null
  );
  const [showConfirm, setShowConfirm] = useState(false);
  const [emailStudent, setEmailStudent] = useState(false);

  // AI prompt scaffolding fields
  const [rubric, setRubric] = useState("Clarity (1-5), accuracy (1-5), depth of integration (1-5).");

  const generateAI = async () => {
    setAiBusy(true);
    try {
      const r = await universityApi.feedbackAIDraft({
        submission_id: submission.id,
        assignment_title: submission.title,
        assignment_instructions: "",
        rubric,
        correct_answer_or_objectives: "",
        drafted_by: adminEmail,
      });
      const f = r.data?.feedback;
      setFeedbackText(f.student_visible_text || "");
      setFbId(f.id);
      setFbStatus(f.status);
      setAiInfo({ model: f.ai_model, at: f.ai_generated_at });
      toast.success("AI draft generated — review before publishing");
    } catch (e) { toast.error(formatApiError(e, "AI draft failed")); }
    finally { setAiBusy(false); }
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const r = await universityApi.feedbackUpsert({
        submission_id: submission.id,
        student_visible_text: feedbackText,
        instructor_private_notes: privateNotes,
        drafted_by: adminEmail,
      });
      const f = r.data?.feedback;
      setFbId(f.id);
      setFbStatus(f.status);
      setAiInfo(null);            // human-edited → no longer AI-only
      toast.success("Feedback draft saved");
    } catch (e) { toast.error(formatApiError(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    if (!fbId) {
      // Make sure draft is saved before publishing
      await saveDraft();
    }
    setShowConfirm(true);
  };

  const confirmPublish = async () => {
    try {
      // Save latest text first
      const r = await universityApi.feedbackUpsert({
        submission_id: submission.id,
        student_visible_text: feedbackText,
        instructor_private_notes: privateNotes,
        drafted_by: adminEmail,
      });
      const f = r.data?.feedback;
      await universityApi.feedbackPublish({
        feedback_id: f.id,
        approved_by: adminEmail,
        email_student: emailStudent,
      });
      toast.success(`Feedback published${emailStudent ? " · emailed student" : ""}`);
      setShowConfirm(false);
      onChanged();
    } catch (e) { toast.error(formatApiError(e, "Publish failed")); }
  };

  const returnForRevision = async () => {
    if (!feedbackText.trim()) return toast.error("Write a revision request first");
    try {
      const r = await universityApi.feedbackUpsert({
        submission_id: submission.id,
        student_visible_text: feedbackText,
        instructor_private_notes: privateNotes,
        drafted_by: adminEmail,
      });
      await universityApi.feedbackReturnRevision({
        feedback_id: r.data.feedback.id,
        revision_request: feedbackText,
        approved_by: adminEmail,
      });
      toast.success("Returned for revision");
      onChanged();
    } catch (e) { toast.error(formatApiError(e, "Return failed")); }
  };

  const gradeNow = async () => {
    if (!score) return toast.error("Enter a score first");
    setGradingBusy(true);
    try {
      const r = await universityApi.gradingUpsertGrade({
        submission_id: submission.id,
        score: Number(score),
        max_score: Number(maxScore) || 100,
        pass_threshold: Number(pass) || 70,
        graded_by: adminEmail,
        ce_hours_awarded: Number(ceHrs) || 0,
        clock_hours_awarded: Number(clockHrs) || 0,
      });
      toast.success(`Grade saved · ${r.data.grade.passed ? "PASS" : "FAIL"}`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e, "Grade failed")); }
    finally { setGradingBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4" data-testid="grading-drawer">
      <div className="bg-white w-full max-w-3xl rounded-t-2xl md:rounded-2xl shadow-xl max-h-[92vh] overflow-y-auto">
        <header className="sticky top-0 bg-white border-b border-slate-100 px-5 py-3 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">{submission.course_code} · {submission.kind}</div>
            <div className="font-display text-lg text-slate-900 truncate">{submission.title || "(untitled)"}</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="grading-drawer-close">
            <XCircle className="w-5 h-5" />
          </button>
        </header>

        <div className="px-5 py-5 space-y-6">
          {/* Submission content */}
          <section>
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Student submission</div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800 whitespace-pre-wrap" data-testid="grading-submission-content">
              {submission.content || "(no content provided)"}
            </div>
          </section>

          {/* Grade */}
          <section className="rounded-xl border border-slate-200 p-4">
            <div className="text-xs uppercase tracking-wide text-purple-900 font-semibold mb-3">Grade</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Field label="Score">
                <input type="number" className="form-input" value={score} onChange={(e) => setScore(e.target.value)} data-testid="grading-score-input" />
              </Field>
              <Field label="Max">
                <input type="number" className="form-input" value={maxScore} onChange={(e) => setMaxScore(e.target.value)} data-testid="grading-max-input" />
              </Field>
              <Field label="Pass ≥">
                <input type="number" className="form-input" value={pass} onChange={(e) => setPass(e.target.value)} data-testid="grading-pass-input" />
              </Field>
              <Field label="CE Hours">
                <input type="number" step="0.25" className="form-input" value={ceHrs} onChange={(e) => setCeHrs(e.target.value)} data-testid="grading-ce-input" />
              </Field>
              <Field label="Clock Hours">
                <input type="number" step="0.25" className="form-input" value={clockHrs} onChange={(e) => setClockHrs(e.target.value)} data-testid="grading-clock-input" />
              </Field>
            </div>
            <div className="mt-3 flex justify-end">
              <button
                onClick={gradeNow}
                disabled={gradingBusy}
                className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white text-sm h-9 px-4 inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="grading-save-grade-btn"
              >
                {gradingBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
                Save Grade
              </button>
            </div>
          </section>

          {/* Feedback */}
          <section className="rounded-xl border border-slate-200 p-4" data-testid="grading-feedback-block">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-wide text-purple-900 font-semibold">Instructor Feedback</div>
              <div className="flex items-center gap-2 text-[11px] text-slate-500">
                {fbStatus && (
                  <span className={`px-2 py-0.5 rounded-full ${fbStatus === "published" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                    {fbStatus.toUpperCase()}
                  </span>
                )}
                {aiInfo && (
                  <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-900 inline-flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />AI draft · {aiInfo.model}
                  </span>
                )}
              </div>
            </div>

            <Field label="Rubric / objectives for AI">
              <textarea
                className="form-input min-h-[60px]"
                value={rubric}
                onChange={(e) => setRubric(e.target.value)}
                data-testid="grading-rubric-input"
              />
            </Field>

            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <button
                onClick={generateAI}
                disabled={aiBusy}
                className="rounded-xl border border-purple-300 bg-purple-50 hover:bg-purple-100 text-purple-900 text-sm h-9 px-3 inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="grading-ai-draft-btn"
              >
                {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Generate AI Feedback Draft
              </button>
              <div className="text-[11px] text-slate-500">
                Powered by Claude Sonnet 4.5 · stored as DRAFT until you publish
              </div>
            </div>

            <div className="mt-4">
              <Field label="Student-visible feedback (this is what the student will see when published)">
                <textarea
                  className="form-input min-h-[140px]"
                  value={feedbackText}
                  onChange={(e) => { setFeedbackText(e.target.value); setAiInfo(null); }}
                  placeholder="Write your feedback here, or click the AI button above to generate a draft you can edit."
                  data-testid="grading-feedback-input"
                />
              </Field>
            </div>

            <div className="mt-3">
              <Field label="Private instructor notes (NEVER shown to student)">
                <textarea
                  className="form-input min-h-[80px]"
                  value={privateNotes}
                  onChange={(e) => setPrivateNotes(e.target.value)}
                  placeholder="Internal notes only — student will never see this."
                  data-testid="grading-private-notes-input"
                />
              </Field>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2 justify-end border-t border-slate-100 pt-3">
              <button
                onClick={saveDraft}
                disabled={saving}
                className="rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-800 text-sm h-9 px-3 inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="grading-save-draft-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save as Draft
              </button>
              <button
                onClick={returnForRevision}
                className="rounded-xl border border-amber-300 hover:bg-amber-50 text-amber-800 text-sm h-9 px-3 inline-flex items-center gap-2"
                data-testid="grading-return-revision-btn"
              >
                <RefreshCcw className="w-4 h-4" />
                Return for Revision
              </button>
              <button
                onClick={publish}
                className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white text-sm h-9 px-4 inline-flex items-center gap-2"
                data-testid="grading-publish-btn"
              >
                <Send className="w-4 h-4" />
                Publish Feedback to Student
              </button>
            </div>
          </section>
        </div>
      </div>

      {showConfirm && (
        <PublishConfirmModal
          studentName={student?.student_name}
          emailStudent={emailStudent}
          setEmailStudent={setEmailStudent}
          onCancel={() => setShowConfirm(false)}
          onConfirm={confirmPublish}
        />
      )}
    </div>
  );
}

function PublishConfirmModal({ studentName, emailStudent, setEmailStudent, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="publish-confirm-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5">
        <div className="inline-flex items-center gap-2 mb-2">
          <AlertCircle className="w-5 h-5 text-amber-600" />
          <div className="font-display text-lg text-slate-900">Publish feedback?</div>
        </div>
        <p className="text-sm text-slate-700">
          This feedback will become visible to <b>{studentName || "the student"}</b> on
          their portal. Make sure you&apos;ve reviewed the AI-generated content before publishing.
        </p>
        <label className="mt-3 flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid="publish-email-checkbox-label">
          <input
            type="checkbox"
            checked={emailStudent}
            onChange={(e) => setEmailStudent(e.target.checked)}
            data-testid="publish-email-checkbox"
          />
          <Mail className="w-4 h-4 text-slate-500" />
          Email student a copy of this feedback
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} className="rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm h-9 px-4" data-testid="publish-confirm-cancel">
            Cancel
          </button>
          <button onClick={onConfirm} className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium h-9 px-4 inline-flex items-center gap-2" data-testid="publish-confirm-confirm">
            <CheckCircle2 className="w-4 h-4" />Publish
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function Stat({ label, value, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    green: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    rose:  "bg-rose-50 text-rose-800 border-rose-200",
  };
  return (
    <div className={`rounded-lg border px-2 py-1.5 ${tones[tone]}`}>
      <div className="text-[9px] uppercase tracking-wide">{label}</div>
      <div className="text-base font-semibold">{value}</div>
    </div>
  );
}
